import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { Book } from '../types';

/** Identifiant stable d'une édition dans le panier. */
export const bookId = (book: Book): number => book.id;

/**
 * Une édition est « hors-série » uniquement si son numéro porte le marqueur
 * « Hors-série » persisté en base (flag explicite `hors_serie` dans
 * editions.json, voir les scripts d'import). Un numéro vide ne suffit pas :
 * certaines éditions au numéro inconnu ne sont pas des hors-série.
 */
export const isHorsSerie = (book: Book): boolean => {
  const n = (book.edition_num || '').trim().toLowerCase();
  return n === 'hors-série' || n === 'hors-serie' || n === 'hs';
};

/** Valeur numérique du prix (0 si non renseigné ou invalide). */
export const priceValue = (book: Book): number => {
  const n = Number(book.price);
  return Number.isFinite(n) ? n : 0;
};

const currencySymbol = (currency?: string): string =>
  currency === 'EUR' || !currency ? '€' : currency;

/** Prix formaté pour l'affichage (ex. « 10 € », « 12,50 € »). */
export const formatBookPrice = (book: Book): string => {
  if (book.price == null || book.price === ('' as unknown)) return '';
  const n = Number(book.price);
  if (!Number.isFinite(n)) return '';
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
  return `${s} ${currencySymbol(book.currency)}`;
};

interface CartContextType {
  items: Book[];
  count: number;
  total: number;
  isInCart: (book: Book) => boolean;
  addItem: (book: Book) => void;
  removeItem: (book: Book) => void;
  toggleItem: (book: Book) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const STORAGE_KEY = 'edition_cart';

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<Book[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? (JSON.parse(saved) as Book[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const isInCart = useCallback(
    (book: Book) => items.some((b) => bookId(b) === bookId(book)),
    [items]
  );

  const addItem = useCallback((book: Book) => {
    setItems((prev) =>
      prev.some((b) => bookId(b) === bookId(book)) ? prev : [...prev, book]
    );
  }, []);

  const removeItem = useCallback((book: Book) => {
    setItems((prev) => prev.filter((b) => bookId(b) !== bookId(book)));
  }, []);

  const toggleItem = useCallback((book: Book) => {
    setItems((prev) =>
      prev.some((b) => bookId(b) === bookId(book))
        ? prev.filter((b) => bookId(b) !== bookId(book))
        : [...prev, book]
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = items.reduce((sum, b) => sum + priceValue(b), 0);

  const value: CartContextType = {
    items,
    count: items.length,
    total,
    isInCart,
    addItem,
    removeItem,
    toggleItem,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
